import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BookingTimeSelection = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dateStr = searchParams.get('date');

    const [duration, setDuration] = useState(1);
    // availabilityMap: date string -> status ('available'/'full') for multi-day validation
    const [availabilityMap, setAvailabilityMap] = useState({});
    // timeSlotCounts: time string -> available count for THIS date+duration
    const [timeSlotCounts, setTimeSlotCounts] = useState({});
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    // showRoomCount: controlled by admin toggle in backend Settings
    const [showRoomCount, setShowRoomCount] = useState(false);

    // AM Time Slots (12 AM to 11 AM)
    const amTimeSlots = [
        "12:00 AM", "01:00 AM", "02:00 AM", "03:00 AM", "04:00 AM", "05:00 AM",
        "06:00 AM", "07:00 AM", "08:00 AM", "09:00 AM", "10:00 AM", "11:00 AM"
    ];

    // PM Time Slots (12 PM to 11 PM)
    const pmTimeSlots = [
        "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM",
        "06:00 PM", "07:00 PM", "08:00 PM", "09:00 PM", "10:00 PM", "11:00 PM"
    ];

    const allTimeSlots = [...amTimeSlots, ...pmTimeSlots];

    // Fetch admin setting: should we show room counts on time-slot buttons?
    useEffect(() => {
        axios.get('/api/settings')
            .then(res => {
                if (res.data?.data?.show_timeslot_room_count === true) {
                    setShowRoomCount(true);
                }
            })
            .catch(() => {}); // silent — feature just stays off
    }, []);

    // Fetch the simple date-level availability map (used for multi-day validation)
    const fetchDateAvailability = useCallback(async () => {
        try {
            const response = await axios.get('/api/public/room-availability');
            if (response.data.status === 'success') {
                const map = {};
                response.data.data.forEach(item => {
                    map[item.date] = item.status;
                });
                setAvailabilityMap(map);
            }
        } catch (err) {
            console.error(err);
            setError('Failed to verify availability.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch per-time-slot room counts for the selected date + duration
    const fetchTimeSlotCounts = useCallback(async () => {
        if (!dateStr) return;
        setLoadingSlots(true);
        try {
            // Run all time-slot requests in parallel
            const results = await Promise.all(
                allTimeSlots.map(time =>
                    axios.get(`/api/public/booking/availability?date=${dateStr}&time=${encodeURIComponent(time)}&duration=${duration}`)
                        .then(res => {
                            if (res.data.status === 'success') {
                                // Sum available_count across all buildings/categories
                                let total = 0;
                                (res.data.data || []).forEach(b => {
                                    (b.room_types || []).forEach(rt => {
                                        (rt.categories || []).forEach(cat => {
                                            total += (cat.available_count || 0);
                                        });
                                    });
                                });
                                return { time, count: total };
                            }
                            return { time, count: null };
                        })
                        .catch(() => ({ time, count: null }))
                )
            );
            const countMap = {};
            results.forEach(r => { countMap[r.time] = r.count; });
            setTimeSlotCounts(countMap);
        } catch (err) {
            console.error('Error fetching time slot counts:', err);
        } finally {
            setLoadingSlots(false);
        }
    }, [dateStr, duration]);

    useEffect(() => {
        if (dateStr) {
            fetchDateAvailability();
        }
    }, [dateStr, fetchDateAvailability]);

    // Re-fetch slot counts when date, duration, or showRoomCount changes
    // Only fires API calls when the admin has enabled the feature
    useEffect(() => {
        if (dateStr && showRoomCount) {
            fetchTimeSlotCounts();
        } else {
            setTimeSlotCounts({}); // clear stale counts if feature turned off
        }
    }, [dateStr, duration, showRoomCount, fetchTimeSlotCounts]);

    const handleTimeSelect = (time) => {
        // Validate multi-day availability
        const startDate = new Date(dateStr);
        for (let i = 0; i < duration; i++) {
            const checkDate = new Date(startDate);
            checkDate.setDate(startDate.getDate() + i);
            const year = checkDate.getFullYear();
            const month = String(checkDate.getMonth() + 1).padStart(2, '0');
            const day = String(checkDate.getDate()).padStart(2, '0');
            const checkDateStr = `${year}-${month}-${day}`;

            if (availabilityMap[checkDateStr] !== 'available') {
                alert(`Rooms are not available on ${checkDateStr}. Please reduce duration or choose another date.`);
                return;
            }
        }

        navigate(`/booking-process?date=${dateStr}&time=${time}&duration=${duration}`);
    };

    const formatTimeLabel = (time) => {
        if (time === "12:00 PM") return "12 Noon";
        return time.replace(":00 ", " ");
    };

    const isTimeDisabled = (time) => {
        if (!dateStr) return true;

        const today = new Date();
        const selectedDate = new Date(dateStr);

        const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

        if (selectedDateOnly < todayDateOnly) return true;
        if (selectedDateOnly > todayDateOnly) return false;

        const currentHour = today.getHours();

        let [timePart, modifier] = time.split(' ');
        let [hours] = timePart.split(':');
        hours = parseInt(hours, 10);

        if (hours === 12) {
            hours = modifier === 'PM' ? 12 : 0;
        } else if (modifier === 'PM') {
            hours += 12;
        }

        return hours < currentHour;
    };

    const renderTimeButton = (time) => {
        const disabled = isTimeDisabled(time);

        if (!showRoomCount) {
            // ---- PLAIN MODE (feature OFF): original compact button, no counts ----
            return (
                <button
                    key={time}
                    onClick={() => !disabled && handleTimeSelect(time)}
                    disabled={disabled}
                    style={{
                        width: '100px',
                        height: '50px',
                        borderRadius: '5px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        backgroundColor: disabled ? '#ccc' : '#2e7d32',
                        color: '#fff',
                        border: 'none',
                        opacity: disabled ? 0.6 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    {formatTimeLabel(time)}
                </button>
            );
        }

        // ---- RICH MODE (feature ON): shows count sub-label ----
        const count = timeSlotCounts[time];
        const noRooms = count !== null && count !== undefined && count === 0;
        const isDisabled = disabled || noRooms;

        // Color: grey=past/no-rooms, orange=loading, green=available
        let bgColor = '#ccc';
        if (!disabled) {
            if (loadingSlots) bgColor = '#f0a500';
            else if (noRooms) bgColor = '#ccc';
            else bgColor = '#2e7d32';
        }

        return (
            <button
                key={time}
                onClick={() => !isDisabled && handleTimeSelect(time)}
                disabled={isDisabled}
                title={isDisabled ? (disabled ? 'Past time' : 'No rooms available') : `${count ?? '?'} rooms available`}
                style={{
                    width: '110px',
                    minHeight: '64px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    backgroundColor: bgColor,
                    color: '#fff',
                    border: 'none',
                    opacity: isDisabled ? 0.55 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    padding: '6px 4px'
                }}
            >
                <span>{formatTimeLabel(time)}</span>
                {!disabled && (
                    <span style={{ fontSize: '11px', fontWeight: 'normal', opacity: 0.92 }}>
                        {loadingSlots ? '...' : (count !== null && count !== undefined ? `${count} rooms` : '?')}
                    </span>
                )}
            </button>
        );
    };

    if (!dateStr) return <div className="p-5 text-center text-danger">Invalid Date Selected</div>;
    if (loading) return <div className="p-5 text-center">Loading...</div>;

    const formattedDate = new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <div style={{ background: '#f9f9f9', minHeight: '100vh', paddingTop: '40px', paddingBottom: '40px' }}>
            <div className="container" style={{ maxWidth: '900px', background: '#fff', padding: '30px', borderRadius: '10px', boxShadow: '0 0 15px rgba(0,0,0,0.1)' }}>
                <h2 className="text-center mb-4" style={{ color: '#840000' }}>{formattedDate}</h2>
                <h4 className="text-center mb-2 text-muted">Select Check-in Time &amp; Duration</h4>
                {showRoomCount && (
                    <p className="text-center text-muted mb-4" style={{ fontSize: '0.9rem' }}>
                        Room count shown on each button reflects exact availability for the selected time &amp; duration.
                    </p>
                )}

                <div className="mb-4 text-center">
                    <label className="form-label fw-bold me-3">Select Duration (Max 3 Days):</label>
                    <select
                        className="form-select d-inline-block w-auto"
                        value={duration}
                        onChange={(e) => setDuration(parseInt(e.target.value))}
                        style={{ border: '2px solid #840000', padding: '8px' }}
                    >
                        <option value={1}>1 Day</option>
                        <option value={2}>2 Days</option>
                        <option value={3}>3 Days</option>
                    </select>
                </div>

                {/* AM Section */}
                <div className="mb-5">
                    <h3 className="text-center mb-3 text-secondary">AM</h3>
                    <div className="d-flex flex-wrap gap-3 justify-content-center">
                        {amTimeSlots.map(renderTimeButton)}
                    </div>
                </div>

                {/* PM Section */}
                <div className="mb-5">
                    <h3 className="text-center mb-3 text-secondary">PM</h3>
                    <div className="d-flex flex-wrap gap-3 justify-content-center">
                        {pmTimeSlots.map(renderTimeButton)}
                    </div>
                </div>

                {/* Legend — only shown in rich count mode */}
                {showRoomCount && (
                    <div className="d-flex gap-4 justify-content-center mt-2" style={{ fontSize: '0.82rem', color: '#555' }}>
                        <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#2e7d32', borderRadius: 3, marginRight: 5 }}></span>Available</span>
                        <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#ccc', borderRadius: 3, marginRight: 5 }}></span>No Rooms / Past</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookingTimeSelection;

